import React, { useEffect, useState } from "react";
import tenantUserServices from '../../../services/clientAdmin/tenantUserServices';
import Page from '../../../components/Page';

const TABS = [
    { key: "all", label: "All" },
    { key: "scheduled", label: "Scheduled" },
    { key: "unscheduled", label: "Unscheduled" },
];

const STATUS_META = {
    ONGOING: { dot: "bg-green-500", pill: "bg-green-50 text-green-700 ring-1 ring-green-200" },
    UPCOMING: { dot: "bg-blue-500", pill: "bg-blue-50 text-blue-700 ring-1 ring-blue-200" },
    EXPIRED: { dot: "bg-red-500", pill: "bg-red-50 text-red-700 ring-1 ring-red-200" },
    UNSCHEDULED: { dot: "bg-gray-400", pill: "bg-gray-100 text-gray-600 ring-1 ring-gray-200" },
};

const AssessmentScheduleList = () => {
    const [assessments, setAssessments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");

    const [page, setPage] = useState(1);
    const [limit] = useState(20);
    const [tab, setTab] = useState("all");

    const [searchInput, setSearchInput] = useState("");
    const [search, setSearch] = useState("");
    

    useEffect(() => {
        const handle = setTimeout(() => {
            setSearch(searchInput.trim());
            setPage(1);
        }, 400);
        return () => clearTimeout(handle);
    }, [searchInput]);

    const fetchAssessments = async () => {
        try {
            setLoading(true);
            setError("");
            const response = await tenantUserServices.getScheduleDetails(tab, searchInput,page, limit);
            if (!response.ok) {
                throw new Error("Failed to fetch assessment schedules");
            }

            setAssessments(response.data);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchAssessments();
    }, [page, tab, search]);

    const handleTabChange = (nextTab) => {
        if (nextTab === tab) return;
        setTab(nextTab);
        setPage(1);
    };

    const statusMeta = (status) => STATUS_META[status] || STATUS_META.UNSCHEDULED;

    const formatDate = (value) =>
        value
            ? new Date(value).toLocaleString(undefined, {
                  dateStyle: "medium",
                  timeStyle: "short",
              })
            : "—";

    return (
        <Page title="">
            <div className="p-6 bg-gray-50 min-h-screen">
                <div className="max-w-7xl mx-auto">

                    <div className="mb-6">
                        <h1 className="text-2xl font-bold text-gray-800">
                            Assessment Schedule List
                        </h1>
                        <p className="text-sm text-gray-500 mt-1">
                            View and filter assessments assigned to users, scheduled or not.
                        </p>
                    </div>

                    <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
                        <div className="flex items-center gap-1 bg-gray-100 rounded-full p-1 w-fit">
                            {TABS.map((t) => (
                                <button
                                    key={t.key}
                                    onClick={() => handleTabChange(t.key)}
                                    className={`
                                        px-4 py-2 text-sm font-medium rounded-full transition-colors
                                        ${
                                            tab === t.key
                                                ? "bg-purple-600 text-white shadow-sm"
                                                : "text-gray-500 hover:text-gray-700"
                                        }
                                    `}
                                >
                                    {t.label}
                                </button>
                            ))}
                        </div>

                        <div className="relative w-full sm:w-72">
                            <input
                                type="text"
                                value={searchInput}
                                onChange={(e) => setSearchInput(e.target.value)}
                                placeholder="Search by name or email..."
                                className="w-full px-4 py-2 pr-9 text-sm rounded-lg bg-white ring-1 ring-gray-200 focus:ring-2 focus:ring-purple-400 outline-none transition-shadow"
                            />
                            {searchInput && (
                                <button
                                    onClick={() => setSearchInput("")}
                                    className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                    aria-label="Clear search"
                                >
                                    ×
                                </button>
                            )}
                        </div>
                    </div>

                    {error && (
                        <div className="bg-red-50 text-red-700 ring-1 ring-red-200 p-4 rounded-lg mb-4 text-sm">
                            {error}
                        </div>
                    )}

                    <div className="bg-white rounded-xl shadow-sm ring-1 ring-gray-100 overflow-hidden">
                        <table className="w-full text-sm">
                            <thead className="bg-gray-50 border-b border-gray-200 sticky top-0">
                                <tr>
                                    <th className="px-5 py-3 text-left font-semibold text-gray-600">Assessment</th>
                                    <th className="px-5 py-3 text-left font-semibold text-gray-600">User</th>
                                    <th className="px-5 py-3 text-left font-semibold text-gray-600">Email</th>
                                    <th className="px-5 py-3 text-left font-semibold text-gray-600">Scheduled From</th>
                                    <th className="px-5 py-3 text-left font-semibold text-gray-600">Scheduled To</th>
                                    <th className="px-5 py-3 text-left font-semibold text-gray-600">Status</th>
                                </tr>
                            </thead>

                            <tbody>
                                {loading &&
                                    Array.from({ length: 6 }).map((_, i) => (
                                        <tr key={`skeleton-${i}`} className="border-b border-gray-100 animate-pulse">
                                            {Array.from({ length: 6 }).map((__, j) => (
                                                <td key={j} className="px-5 py-4">
                                                    <div className="h-3.5 bg-gray-200 rounded w-3/4" />
                                                </td>
                                            ))}
                                        </tr>
                                    ))}

                                {!loading && !error && assessments.length === 0 && (
                                    <tr>
                                        <td colSpan="6" className="text-center py-14">
                                            <div className="text-gray-400 text-sm">
                                                No {tab !== "all" ? tab : ""} assessment schedules found
                                                {search ? ` for "${search}"` : ""}
                                            </div>
                                        </td>
                                    </tr>
                                )}

                                {!loading &&
                                    !error &&
                                    assessments.map((item) => {
                                        const meta = statusMeta(item.status);
                                        return (
                                            <tr
                                                key={`${item.assessmentId}-${item.userId}`}
                                                className="border-b border-gray-100 hover:bg-gray-50 transition-colors"
                                            >
                                                <td className="px-5 py-4">
                                                    <div className="font-semibold text-gray-800">
                                                        {item.assessmentName}
                                                    </div>
                                                </td>

                                                <td className="px-5 py-4">
                                                    <div className="font-medium text-gray-700">{item.userName}</div>
                                                </td>

                                                <td className="px-5 py-4 text-gray-600">{item.email}</td>

                                                <td className="px-5 py-4 text-gray-600">
                                                    {formatDate(item.scheduledFrom)}
                                                </td>

                                                <td className="px-5 py-4 text-gray-600">
                                                    {formatDate(item.scheduledTo)}
                                                </td>

                                                <td className="px-5 py-4">
                                                    <span
                                                        className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${meta.pill}`}
                                                    >
                                                        <span className={`w-1.5 h-1.5 rounded-full ${meta.dot}`} />
                                                        {item.status}
                                                    </span>
                                                </td>
                                            </tr>
                                        );
                                    })}
                            </tbody>
                        </table>
                    </div>

                    <div className="flex justify-end items-center gap-3 mt-5">
                        <button
                            disabled={page === 1 || loading}
                            onClick={() => setPage((p) => p - 1)}
                            className="px-4 py-2 rounded-lg bg-gray-200 text-gray-700 disabled:opacity-50 hover:bg-gray-300 transition-colors"
                        >
                            Previous
                        </button>

                        <span className="px-4 py-2 bg-white rounded-lg shadow-sm ring-1 ring-gray-100 text-gray-600">
                            Page {page}
                        </span>

                        <button
                            disabled={assessments.length < limit || loading}
                            onClick={() => setPage((p) => p + 1)}
                            className="px-4 py-2 rounded-lg bg-blue-600 text-white disabled:opacity-50 hover:bg-blue-700 transition-colors"
                        >
                            Next
                        </button>
                    </div>

                </div>
            </div>
        </Page>
    );
};

export default AssessmentScheduleList;